import React, { useState, useEffect } from 'react';
import { BRRRRMetrics, Property } from '../../types/property';
import { formatCurrency } from '../../utils/formatting';
import { propertyService } from '../../services/propertyService';

interface BRRRRAnalysisProps {
  brrrr: BRRRRMetrics;
  property: Property;
  onPropertyUpdate: (propertyId: number, updates: Partial<Property>) => void;
}

export const BRRRRAnalysis: React.FC<BRRRRAnalysisProps> = ({ brrrr, property, onPropertyUpdate }) => {
  // Default rehab cost to property.renovations if available, otherwise use brrrr.rehab_cost
  const defaultRehabCost = property.renovations != null ? property.renovations : (brrrr.rehab_cost || 0);
  const defaultPurchasePrice = property.purchase_price ?? property.list_price ?? 0;
  const [customRehabCost, setCustomRehabCost] = useState<string>(defaultRehabCost.toString());
  const [customPurchasePrice, setCustomPurchasePrice] = useState<string>(defaultPurchasePrice.toString());
  const [customARV, setCustomARV] = useState<string>(brrrr.arv.toString());
  const [customRent, setCustomRent] = useState<string>('');
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [justUpdated, setJustUpdated] = useState(false);

  // Sync purchase price from main input to BRRRR section
  useEffect(() => {
    const newPurchasePrice = property.purchase_price ?? property.list_price ?? 0;
    setCustomPurchasePrice(newPurchasePrice.toString());
  }, [property.purchase_price, property.list_price]);

  // Sync rehab cost from main input to BRRRR section
  useEffect(() => {
    const newRehabCost = property.renovations ?? 0;
    setCustomRehabCost(newRehabCost.toString());
  }, [property.renovations]);

  const cashPulledOutPositive = brrrr.cash_pulled_out > 0;

  const warnings = brrrr.warnings || [];
  const hasARVWarning = warnings.includes('arv_below_purchase');
  const hasNegativeEquity = warnings.includes('negative_equity');
  const hasLoanExceedsARV = warnings.includes('loan_exceeds_arv');
  const hasNoCapitalRecovery = warnings.includes('no_capital_recovery');

  const recalculate = async (rehabCost: number, arv: number, rent?: number, purchasePrice?: number) => {
    try {
      setIsRecalculating(true);
      const payload: any = { rehab_cost: rehabCost, arv_override: arv };
      if (rent != null && rent > 0) payload.gross_rent_override = rent;
      const response = await propertyService.calculateMetrics(property.id, payload);
      const updates: any = {
        brrrr: response.metrics.brrrr,
        total_project_cost: response.metrics.total_project_cost,
        estimated_rehab: rehabCost,
      };
      // Only update cash flow card if rent was explicitly overridden by the user.
      // ARV changes affect BRRRR post-refi cash flow but should NOT touch the
      // original-mortgage cash flow shown in the Cash Flow card.
      if (rent != null && rent > 0) {
        updates.monthly_cash_flow = response.metrics.monthly_cash_flow;
        updates.section8_rent = rent;
      }
      // Update purchase price if changed
      if (purchasePrice != null && purchasePrice > 0) {
        updates.purchase_price = purchasePrice;
      }
      onPropertyUpdate(property.id, updates);
      setJustUpdated(true);
      setTimeout(() => setJustUpdated(false), 2000);
    } catch (error) {
      console.error('Error recalculating metrics:', error);
    } finally {
      setIsRecalculating(false);
    }
  };

  const triggerRecalc = () => {
    const rehab = parseFloat(customRehabCost) || 0;
    const arv = parseFloat(customARV) || brrrr.arv;
    const rent = parseFloat(customRent) || undefined;
    const purchasePrice = parseFloat(customPurchasePrice) || undefined;
    recalculate(rehab, arv, rent, purchasePrice);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') triggerRecalc();
  };

  // Use current purchase price (either from input or property)
  const currentPurchasePrice = parseFloat(customPurchasePrice) || property.purchase_price || property.list_price || 0;
  const downPaymentAmount = currentPurchasePrice * brrrr.down_payment_pct / 100;
  const currentOriginalLoan = currentPurchasePrice - downPaymentAmount;
  
  // Use current rehab cost (either from input or property)
  const currentRehabCost = parseFloat(customRehabCost) || property.renovations || 0;
  const currentTotalInvestment = currentPurchasePrice + currentRehabCost;

  return (
    <div className="mt-3 space-y-2">

      {/* ── TOP INPUT PANEL ── */}
      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2">
        <div className="text-xs font-bold text-amber-800 uppercase tracking-wide">
          Your Estimates {isRecalculating && <span className="text-gray-400 font-normal ml-1">⏳ recalculating…</span>}
          {justUpdated && <span className="text-green-600 font-normal ml-1">✓ updated</span>}
        </div>
        <div className="grid grid-cols-4 gap-2">          {/* Purchase Price */}
          <div>
            <label className="block text-[10px] text-amber-700 font-semibold mb-0.5">🏠 Purchase Price</label>
            <div className="flex items-center border border-amber-300 rounded bg-white overflow-hidden focus-within:ring-1 focus-within:ring-amber-500">
              <span className="px-1.5 text-gray-400 text-xs">$</span>
              <input
                type="number"
                value={customPurchasePrice}
                onChange={(e) => setCustomPurchasePrice(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={triggerRecalc}
                className="flex-1 min-w-0 py-1 pr-1.5 text-xs font-semibold focus:outline-none"
                disabled={isRecalculating}
                placeholder="0"
              />
            </div>
            <div className="text-[10px] text-amber-600 mt-0.5">List price: {formatCurrency(property.list_price ?? 0)}</div>
          </div>          {/* Rehab Cost */}
          <div>
            <label className="block text-[10px] text-amber-700 font-semibold mb-0.5">🔨 Rehab Cost</label>
            <div className="flex items-center border border-amber-300 rounded bg-white overflow-hidden focus-within:ring-1 focus-within:ring-amber-500">
              <span className="px-1.5 text-gray-400 text-xs">$</span>
              <input
                type="number"
                value={customRehabCost}
                onChange={(e) => setCustomRehabCost(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={triggerRecalc}
                className="flex-1 min-w-0 py-1 pr-1.5 text-xs font-semibold focus:outline-none"
                disabled={isRecalculating}
                placeholder="0"
              />
            </div>
            <div className="text-[10px] text-amber-600 mt-0.5">Rehab Cost: {formatCurrency(property.renovations ?? 0)}</div>
          </div>
          {/* ARV */}
          <div>
            <label className="block text-[10px] text-amber-700 font-semibold mb-0.5">🏠 After Repair Value (ARV)</label>
            <div className="flex items-center border border-amber-300 rounded bg-white overflow-hidden focus-within:ring-1 focus-within:ring-amber-500">
              <span className="px-1.5 text-gray-400 text-xs">$</span>
              <input
                type="number"
                value={customARV}
                onChange={(e) => setCustomARV(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={triggerRecalc}
                className="flex-1 min-w-0 py-1 pr-1.5 text-xs font-semibold focus:outline-none"
                disabled={isRecalculating}
                placeholder="Enter comps"
              />
            </div>
            <div className="text-[10px] text-amber-600 mt-0.5">List price: {formatCurrency(property.list_price ?? 0)}</div>
          </div>
          {/* Post-reno Rent */}
          <div>
            <div className="flex items-baseline gap-0.5 mb-0.5">
              <label className="text-[10px] text-amber-700 font-semibold">💵 Rent After Reno</label>
              <div className="relative group leading-none">
                <span className="text-amber-400 cursor-help text-[10px] leading-none">ⓘ</span>
                <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-56 bg-gray-900 text-white text-xs rounded-lg p-2.5 shadow-xl text-left">
                  <div className="font-bold mb-1">Post-renovation rent</div>
                  <div className="opacity-90">Use your projected post-renovation rent here. Leave blank to keep the current modeled rent.</div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                </div>
              </div>
            </div>
            <div className="flex items-center border border-amber-300 rounded bg-white overflow-hidden focus-within:ring-1 focus-within:ring-amber-500">
              <span className="px-1.5 text-gray-400 text-xs">$</span>
              <input
                type="number"
                value={customRent}
                onChange={(e) => setCustomRent(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={triggerRecalc}
                className="flex-1 min-w-0 py-1 pr-1.5 text-xs font-semibold focus:outline-none"
                disabled={isRecalculating}
                placeholder={`${property.section8_rent ?? ''}`}
              />
            </div>
            <div className="text-[10px] text-amber-600 mt-0.5">Current estimated rent: {formatCurrency(property.section8_rent ?? 0)}/mo</div>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {brrrr.has_warnings && currentRehabCost > 0 && brrrr.arv !== currentPurchasePrice && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
          <span className="text-amber-700 font-bold shrink-0">⚠️</span>
          {hasARVWarning && <span className="text-amber-800">ARV below purchase price</span>}
          {hasNegativeEquity && <span className="text-amber-800">Negative equity after rehab</span>}
          {hasNoCapitalRecovery && <span className="text-amber-800">Capital stays in deal</span>}
          {hasLoanExceedsARV && <span className="text-amber-800">Refi loan capped at ARV</span>}
        </div>
      )}

      {/* Steps */}
      <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100 text-xs">

        {/* Step 1: BUY */}
        <div className="px-3 py-2 bg-blue-50 space-y-1.5">
          <div className="flex items-center gap-2">
            <span>🏠</span>
            <span className="font-semibold text-blue-900 text-xs">Buy</span>
          </div>
          <div className="ml-5 space-y-1 text-xs text-gray-700">
            <div className="flex justify-between">
              <span className="text-gray-500">Purchase price</span>
              <strong className="text-gray-900">{formatCurrency(currentPurchasePrice)}</strong>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">− Down payment ({brrrr.down_payment_pct}%)</span>
              <span>−{formatCurrency(downPaymentAmount)}</span>
            </div>
            <div className="flex justify-between pt-1 border-t border-blue-200 font-bold text-blue-900">
              <span>= Original loan</span>
              <span>{formatCurrency(currentOriginalLoan)}</span>
            </div>
          </div>
        </div>

        {/* Step 2: REHAB */}
        <div className="px-3 py-2 bg-red-50 space-y-1.5">
          <div className="flex items-center gap-2">
            <span>🔨</span>
            <span className="font-semibold text-red-900 text-xs">Rehab</span>
          </div>
          <div className="ml-5 space-y-1 text-xs text-gray-700">
            <div className="flex justify-between">
              <span className="text-gray-500">Purchase price</span>
              <span>{formatCurrency(currentPurchasePrice)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">+ Rehab / improvements</span>
              <span className="font-semibold">{formatCurrency(currentRehabCost)}</span>
            </div>
            <div className="flex justify-between pt-1 border-t border-red-200 font-bold text-red-900">
              <span>= Total investment</span>
              <span>{formatCurrency(currentTotalInvestment)}</span>
            </div>
          </div>
        </div>

        {/* Step 3: ARV */}
        <div className={`px-3 py-2 space-y-1.5 ${brrrr.potential_equity > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
          <div className="flex items-center gap-2">
            <span>{brrrr.potential_equity > 0 ? '✅' : '❌'}</span>
            <span className={`font-semibold text-xs ${brrrr.potential_equity > 0 ? 'text-green-900' : 'text-red-900'}`}>ARV (After Repair Value)</span>
          </div>
          <div className="ml-5 space-y-1 text-xs text-gray-700">
            <div className="flex justify-between">
              <span className="text-gray-500">ARV estimate</span>
              <span className="font-semibold">{formatCurrency(brrrr.arv)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">− Total investment</span>
              <span>−{formatCurrency(brrrr.total_investment)}</span>
            </div>
            <div className={`flex justify-between pt-1 border-t font-bold ${brrrr.potential_equity > 0 ? 'border-green-200 text-green-800' : 'border-red-200 text-red-800'}`}>
              <span>= Instant equity</span>
              <span>{formatCurrency(brrrr.potential_equity)}</span>
            </div>
          </div>
        </div>

        {/* Step 4: REFINANCE */}
        <div className="px-3 py-2 bg-indigo-50 space-y-1.5">
          <div className="flex items-center gap-2">
            <span>🏦</span>
            <span className="font-semibold text-indigo-900 text-xs">Refinance</span>
          </div>
          <div className="ml-5 space-y-1 text-xs text-gray-700">
            <div className="flex justify-between">
              <span className="text-gray-500">ARV × {brrrr.refinance_ltv}% LTV</span>
              <span className="font-semibold text-indigo-900">{formatCurrency(brrrr.arv)} × {brrrr.refinance_ltv}% = <strong>{formatCurrency(brrrr.refinance_loan_amount)}</strong></span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">− Original loan payoff</span>
              <span>−{formatCurrency(brrrr.original_loan)}</span>
            </div>
            <div className="flex justify-between pt-1 border-t border-indigo-200">
              <span className="text-gray-500">= Gross refi proceeds</span>
              <span className="font-semibold">{formatCurrency(brrrr.gross_refi_proceeds ?? (brrrr.refinance_loan_amount - brrrr.original_loan))}</span>
            </div>
            {(brrrr.refi_closing_costs ?? 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">− Refi closing costs</span>
                <span>−{formatCurrency(brrrr.refi_closing_costs ?? 0)}</span>
              </div>
            )}
            <div className={`flex justify-between pt-1 border-t border-indigo-200 font-bold ${cashPulledOutPositive ? 'text-green-800' : 'text-red-800'}`}>
              <span>{cashPulledOutPositive ? '💰 Net cash pulled out' : '💸 Cash still needed'}</span>
              <span>{formatCurrency(Math.abs(brrrr.cash_pulled_out))}</span>
            </div>
            {brrrr.refinance_monthly_payment != null && (
              <div className="flex justify-between pt-1 border-t border-indigo-100 text-indigo-800">
                <span className="text-gray-500">New monthly P&I payment</span>
                <span className="font-semibold">{formatCurrency(brrrr.refinance_monthly_payment)}/mo</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* OUTCOME */}
      <div className={`rounded-lg border-2 p-3 ${brrrr.has_warnings ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-300'}`}>
        <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">Outcome</div>
        <div className="grid grid-cols-5 gap-0 text-center text-xs divide-x divide-gray-200">

          {/* Cash Out / Cash In Deal */}
          <div className="relative px-1">
            <div className="flex items-center justify-center gap-0.5 text-gray-500 mb-0.5 text-[10px] leading-tight">
              <span>Cash Out</span>
              <div className="relative group">
                <span className="text-gray-400 cursor-help">ⓘ</span>
                <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-64 bg-gray-900 text-white text-xs rounded-lg p-2.5 shadow-xl text-left">
                  <div className="font-bold mb-1">Gross Refi Proceeds − Refi Closing Costs</div>
                  <div className="font-mono text-yellow-200">{formatCurrency(brrrr.gross_refi_proceeds ?? (brrrr.refinance_loan_amount - brrrr.original_loan))} − {formatCurrency(brrrr.refi_closing_costs ?? 0)} = {formatCurrency(brrrr.cash_pulled_out)}</div>
                  <div className="mt-1 opacity-75">{cashPulledOutPositive ? 'Net dollars returned to you after paying off the original loan and refi closing costs.' : 'After paying off the original loan and refi costs, you still have this much cash tied up in the deal.'}</div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                </div>
              </div>
            </div>
            <div className={`text-sm font-black ${cashPulledOutPositive ? 'text-green-700' : 'text-orange-600'}`}>
              {cashPulledOutPositive ? '' : '−'}{formatCurrency(Math.abs(brrrr.cash_pulled_out))}
            </div>
            <div className="text-gray-400 text-[10px]">{cashPulledOutPositive ? '💰 back' : '💸 still in'}</div>
          </div>

          {/* New Mortgage */}
          <div className="relative px-1">
            <div className="flex items-center justify-center gap-0.5 text-gray-500 mb-0.5 text-[10px] leading-tight">
              <span>New Mortgage</span>
              <div className="relative group">
                <span className="text-gray-400 cursor-help">ⓘ</span>
                <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-64 bg-gray-900 text-white text-xs rounded-lg p-2.5 shadow-xl text-left">
                  <div className="font-bold mb-1">ARV × Refinance LTV</div>
                  <div className="font-mono text-yellow-200">{formatCurrency(brrrr.arv)} × {brrrr.refinance_ltv}% = {formatCurrency(brrrr.refinance_loan_amount)}</div>
                  <div className="mt-1 opacity-75">The new loan amount after a cash-out refinance. Lenders typically lend up to 75% of the after-repair value.</div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                </div>
              </div>
            </div>
            <div className="text-sm font-black text-gray-900">{formatCurrency(brrrr.refinance_loan_amount)}</div>
            <div className="text-gray-400 text-[10px]">{brrrr.refinance_ltv}% LTV</div>
          </div>

          {/* Equity Left */}
          <div className="relative px-1">
            <div className="flex items-center justify-center gap-0.5 text-gray-500 mb-0.5 text-[10px] leading-tight">
              <span>Equity Left</span>
              <div className="relative group">
                <span className="text-gray-400 cursor-help">ⓘ</span>
                <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-64 bg-gray-900 text-white text-xs rounded-lg p-2.5 shadow-xl text-left">
                  <div className="font-bold mb-1">ARV − New Mortgage</div>
                  <div className="font-mono text-yellow-200">{formatCurrency(brrrr.arv)} − {formatCurrency(brrrr.refinance_loan_amount)} = {formatCurrency(brrrr.equity_remaining)}</div>
                  <div className="mt-1 opacity-75">The equity you retain in the property after refinancing. This is your cushion against value drops and future selling costs.</div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                </div>
              </div>
            </div>
            <div className="text-sm font-black text-gray-900">{formatCurrency(brrrr.equity_remaining)}</div>
            <div className="text-gray-400 text-[10px]">ARV − loan</div>
          </div>

          {/* Equity Increase */}
          {(() => {
            const equityBefore = currentPurchasePrice - currentOriginalLoan;
            const equityAfter = brrrr.equity_remaining;
            const equityGain = equityAfter - equityBefore;
            const isGain = equityGain >= 0;
            return (
              <div className="relative px-1">
                <div className="flex items-center justify-center gap-0.5 text-gray-500 mb-0.5 text-[10px] leading-tight">
                  <span>Equity Gain</span>
                  <div className="relative group">
                    <span className="text-gray-400 cursor-help">ⓘ</span>
                    <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-64 bg-gray-900 text-white text-xs rounded-lg p-2.5 shadow-xl text-left">
                      <div className="font-bold mb-1">Equity After Refi − Equity Before BRRRR</div>
                      <div className="font-mono text-yellow-200">{formatCurrency(equityAfter)} − {formatCurrency(equityBefore)} = {isGain ? '+' : ''}{formatCurrency(equityGain)}</div>
                      <div className="mt-1 opacity-75">Before: {formatCurrency(currentPurchasePrice)} purchase − {formatCurrency(currentOriginalLoan)} loan = {formatCurrency(equityBefore)} equity (your down payment). After: {formatCurrency(brrrr.arv)} ARV − {formatCurrency(brrrr.refinance_loan_amount)} new mortgage = {formatCurrency(equityAfter)} equity. The rehab created {formatCurrency(equityGain)} in new equity.</div>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                    </div>
                  </div>
                </div>
                <div className={`text-sm font-black ${isGain ? 'text-green-700' : 'text-red-600'}`}>
                  {isGain ? '+' : ''}{formatCurrency(equityGain)}
                </div>
                <div className="text-gray-400 text-[10px]">{isGain ? '📈 created' : '📉 lost'}</div>
              </div>
            );
          })()}

          {/* Cash Flow */}
          <div className="relative px-1">
            <div className="flex items-center justify-center gap-0.5 text-gray-500 mb-0.5 text-[10px] leading-tight">
              <span>Cash Flow</span>
              <div className="relative group">
                <span className="text-gray-400 cursor-help">ⓘ</span>
                <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-60 bg-gray-900 text-white text-xs rounded-lg p-2.5 shadow-xl text-left">
                  <div className="font-bold mb-1">Rent − Refi Mortgage − All Operating Expenses</div>
                  <div className="font-mono text-yellow-200">{formatCurrency(brrrr.monthly_cash_flow)}/mo × 12 = {formatCurrency(brrrr.annual_cash_flow)}/yr</div>
                  <div className="mt-1 opacity-75">Uses the post-refi mortgage ({brrrr.refinance_monthly_payment != null ? formatCurrency(brrrr.refinance_monthly_payment) + '/mo' : 'recalculated'} on the {brrrr.refinance_ltv}% LTV loan) — not the original purchase mortgage.{customRent ? ' Using your post-renovation rent.' : ' Using the current estimated rent.'}</div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                </div>
              </div>
            </div>
            <div className={`text-sm font-black ${brrrr.monthly_cash_flow >= 0 ? 'text-green-700' : 'text-red-600'}`}>
              {formatCurrency(brrrr.monthly_cash_flow)}/mo
            </div>
            <div className="text-gray-400 text-[10px]">{formatCurrency(brrrr.annual_cash_flow)}/yr</div>
          </div>

        </div>
      </div>
    </div>
  );
};